"use client"

/* ds-typo-ignore-file — EN-only prototype ad LP: intentional sub-DS sizes (not shipped DS surfaces) */

import Link from "next/link"
import { bloodPriceArg } from "@/lib/blood-price-arg"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { blobMediaUrl } from "@/lib/blob-url"
import { goKeys as k } from "@/i18n/go-keys"
import { homeKeys as hk } from "@/i18n/home-keys"
import { Button, buttonVariants } from "@/components/ui/button"
import { CheckCircleIcon } from "@/components/ui/icons"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { FaqAccordion } from "@/components/ui/faq-accordion"
import { FAQ_KEYS } from "./metabolism-data"
import {
  SECTION,
  BookingLink,
  CtaLabel,
  CtaReassurance,
  useBookingHref,
} from "./metabolism-components"
import { SIGNS } from "./metabolism-data"
import { SignGlyph } from "./metabolism-sign-glyphs"

/* ── 3. Signs — symptom cards (design ported 1:1 from /go/immunity), closed by
   the "stop guessing" wedge ── */
export function MetabolismSigns() {
  const t = useTranslations("go")
  return (
    <section className="py-28 md:py-40">
      <div className={`${SECTION} flex flex-col items-center gap-12`}>
        {/* header — eyebrow + heading */}
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="type-label-md text-neutral-600 uppercase">
            {t(k.metabolismSignsEyebrow)}
          </p>
          <h2 className="type-display-md max-w-[600px] text-neutral-900">
            {t(k.metabolismSignsHeading)}
          </h2>
          <p className="type-body-xl max-w-[620px] text-neutral-500">
            {t(k.metabolismSignsBody)}
          </p>
        </div>

        {/* cards */}
        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SIGNS.map((s) => (
            <div
              key={s.titleKey}
              className="flex flex-col items-center gap-4 rounded-[24px] bg-white p-10 text-center"
            >
              <span className="flex items-center justify-center text-neutral-900">
                <s.Icon className="h-12 w-12" />
              </span>
              {/* reserve 2 lines (top-aligned) so single-line titles keep a
                  consistent icon-to-title gap and the description still starts
                  on the same baseline across every card */}
              <h3 className="type-title-lg flex min-h-[52px] max-w-[200px] items-start justify-center text-neutral-900">
                {t(s.titleKey)}
              </h3>
              <p className="type-body-xl text-neutral-500">{t(s.descKey)}</p>
            </div>
          ))}
        </div>

        {/* closing — "stop guessing, start knowing" wedge, merged into the signs close: icon kept, big heading, no eyebrow */}
        <div className="mt-16 mb-12 flex flex-col items-center gap-5 text-center md:mt-28 md:mb-24">
          <span className="text-neutral-900">
            <SignGlyph name="brain" />
          </span>
          <p className="type-display-md max-w-[760px] text-neutral-900">
            {t(k.metabolismWedgeHeading1)}
            <span className="text-bright-blue-600 block">
              {t(k.metabolismWedgeHeading2)}
            </span>
          </p>
          <p className="type-title-lg max-w-[820px] font-medium text-neutral-600">
            {t(k.metabolismWedgeBody)}
          </p>
        </div>
      </div>
    </section>
  )
}

/* ── 7d. Your report (visual anchor — wire real ReportMockupIpad later) ── */
export function MetabolismReport() {
  const t = useTranslations("go")
  const lp = useLocalePath()
  return (
    <section className="pt-2 pb-6 md:pt-4 md:pb-8">
      <div className={SECTION}>
        <div className="relative overflow-hidden rounded-[32px] bg-white">
          <div className="grid items-stretch md:grid-cols-[0.82fr_1.18fr]">
            <div className="flex flex-col justify-center px-8 py-12 md:px-14 md:py-20 lg:px-16">
              <p className="type-label-md text-neutral-600 uppercase">
                {t(k.metabolismReportEyebrow)}
              </p>
              <h2 className="type-display-md mt-3 max-w-[420px] text-neutral-900">
                {t(k.metabolismReportHeading)}
              </h2>
              <p className="type-body-lg mt-5 max-w-[460px] text-neutral-600">
                {t(k.metabolismReportBody)}
              </p>
              <div className="mt-10">
                <Link
                  href={lp("/report")}
                  className="block w-full sm:inline-block sm:w-auto"
                >
                  <Button variant="tonal" className="w-full sm:w-auto">
                    {t(k.metabolismReportCta)}
                  </Button>
                </Link>
              </div>
            </div>
            {/* right — iPad report mockup, bleeds into the panel's right + bottom edges (cropped flush, no padding) */}
            <div className="relative min-h-[440px] md:min-h-[600px]">
              <div className="absolute top-10 left-0 w-[135%] md:top-14">
                <ReportMockupIpad />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── 9c. Why not just my GP? — "what you get back" report comparison, reusing
    the two-column layout from /compare-health-check-ups-switzerland: a white
    highlighted "Ahead" card (green checks + CTA + report mockup) beside a muted
    "Typical" column (✗ rows + a plain paper report). Placed before pricing as
    the last objection-handler. Prototype copy, hardcoded EN. ── */
const GP_AHEAD_ROW_KEYS = [
  k.metabolismGpAheadRow1,
  k.metabolismGpAheadRow2,
  k.metabolismGpAheadRow3,
  k.metabolismGpAheadRow4,
  k.metabolismGpAheadRow5,
  k.metabolismGpAheadRow6,
] as const

const GP_TYPICAL_ROW_KEYS = [
  k.metabolismGpTypicalRow1,
  k.metabolismGpTypicalRow2,
  k.metabolismGpTypicalRow3,
  k.metabolismGpTypicalRow4,
  k.metabolismGpTypicalRow5,
  k.metabolismGpTypicalRow6,
] as const

export function MetabolismGpCompare() {
  const t = useTranslations("go")
  return (
    <section className="bg-neutral-50 py-16 md:py-24">
      <div className={SECTION}>
        <h2 className="type-display-md mx-auto max-w-[820px] text-center text-neutral-900">
          {t(k.metabolismGpHeading)}
        </h2>
        <p className="type-body-xl mx-auto mt-4 max-w-[560px] text-center text-neutral-500">
          {t(k.metabolismGpBody)}
        </p>

        {/* Gray panel — white highlighted "Ahead" card beside the muted
            "Typical" column, which sits in a matching (invisible) footprint so
            the two read as a balanced pair. Both column contents are vertically
            centred within the equal-height cells. */}
        <div className="mx-auto mt-12 max-w-[1100px] rounded-[32px] bg-neutral-100 p-4 md:p-6">
          <div className="grid items-stretch gap-4 md:grid-cols-2 md:gap-6">
            {/* left — Ahead, highlighted white card */}
            <div className="flex flex-col justify-center rounded-[24px] bg-white px-7 py-10 md:px-10 md:py-12">
              <h3 className="type-title-lg text-neutral-900">
                {t(k.metabolismGpAheadTitle)}
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {GP_AHEAD_ROW_KEYS.map((rowKey) => (
                  <li key={rowKey} className="flex items-center gap-3">
                    <CheckCircleIcon size={22} />
                    <span className="type-body-lg text-neutral-900">
                      {t(rowKey)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* right — Typical, centred in a matching invisible-card footprint */}
            <div className="flex flex-col justify-center px-7 py-10 md:px-10 md:py-12">
              <h3 className="type-title-lg text-neutral-700">
                {t(k.metabolismGpTypicalTitle)}
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {GP_TYPICAL_ROW_KEYS.map((rowKey) => (
                  <li key={rowKey} className="flex items-center gap-3">
                    {/* long muted dash, aligned to the check's slot */}
                    <span
                      aria-hidden
                      className="h-0.5 w-[22px] shrink-0 rounded-full bg-neutral-300"
                    />
                    <span className="type-body-lg text-neutral-500">
                      {t(rowKey)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── 10. The offer — blood vs blood+MRI, using the IMMUNITY pricing
    component (BloodTrackingComparison, blood-mri variant) on a dark
    section. Prices come from the live Stripe feed via useLivePrices.
    TODO: move copy to the `go` namespace. ── */
export function MetabolismPricing() {
  const t = useTranslations("go")
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const lp = useLocalePath()
  return (
    <section
      id="pricing"
      className="bg-deep-blue-950 relative scroll-mt-24 overflow-hidden text-white"
    >
      {/* Mountain-range backdrop — slightly blurred, with a neutral dark
          shade over it so the white heading + cards stay legible. */}
      <div aria-hidden className="absolute inset-0">
        <Image
          src={blobMediaUrl("/images/metabolism/pricing-mountain-range.webp")}
          alt=""
          fill
          sizes="100vw"
          className="scale-105 object-cover object-center blur-sm"
        />
        <div className="absolute inset-0 bg-neutral-950/60" />
      </div>
      <div className="relative z-10 mx-auto max-w-[1100px] px-6 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-[720px] text-center">
          <h2 className="type-display-md text-white">
            {t(k.metabolismPricingHeading)}
          </h2>
          <p className="type-body-lg mx-auto mt-5 max-w-[560px] text-white/60">
            {t(k.metabolismPricingBody)}
          </p>
        </div>

        {/* Immunity pricing component (BloodTrackingComparison) in its
            blood-vs-MRI variant: the white baseline "bloods" card + the
            gradient highlighted "bloods + full-body MRI" card. */}
        <div className="mt-14">
          <BloodTrackingComparison
            offer="blood-mri"
            trackingLocation="go/metabolism"
            baselineCtaLabel={t(k.metabolismPricingBaselineCta)}
            mriHref={lp("/go/fullbodymri")}
            mriPriceFrom={t(k.metabolismPricingMriPriceFrom)}
            mriPrice={t(k.metabolismPricingMriPrice, {
              price: prices.fullPrices.advanced ?? "",
            })}
            mriRows={[
              t(k.metabolismPricingMriRow1),
              t(k.metabolismPricingMriRow2),
              t(k.metabolismPricingMriRow3),
              t(k.metabolismPricingMriRow4),
            ]}
          />
        </div>

        <p className="mx-auto mt-10 max-w-[680px] text-center text-base leading-relaxed font-medium text-white/70">
          {t(k.metabolismPricingFootnote)}
        </p>
      </div>
    </section>
  )
}

/* ── 12. FAQ — same set + layout as /go/bloodtest (home namespace) ── */
export function MetabolismFaq() {
  const ht = useTranslations("home")
  const lp = useLocalePath()
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-10 md:grid-cols-[1fr_2fr] md:gap-12">
        <div>
          <h2 className="type-display-md text-neutral-900">
            {ht(hk.faqHeading)}
          </h2>
          <Link
            href={lp("/faq")}
            className="border-aubergine-800/30 text-aubergine-800 hover:bg-aubergine-800 hover:border-aubergine-800 type-title-sm mt-4 inline-flex items-center justify-center gap-1.5 rounded-full border px-5 py-2.5 transition-colors hover:text-white"
          >
            {ht(hk.faqLink)} &rarr;
          </Link>
        </div>
        <div>
          <FaqAccordion
            items={FAQ_KEYS.map((faq) => ({
              question: ht(faq.q),
              answer: ht(faq.a),
            }))}
          />
        </div>
      </div>
    </section>
  )
}

/* ── 13. Closing CTA — simple metabolism-focused call to action over a
    full-bleed photo. Single booking CTA, no membership. ── */
export function MetabolismClosingCta() {
  const t = useTranslations("go")
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const bookingHref = useBookingHref()
  return (
    <section className="relative isolate flex min-h-[620px] items-center overflow-hidden text-white md:min-h-[720px]">
      <Image
        src={blobMediaUrl("/images/metabolism/closing-cta-metabolism.webp")}
        alt=""
        aria-hidden
        fill
        sizes="100vw"
        className="object-cover"
      />
      {/* dark scrim keeps the white copy legible over the photo */}
      <div aria-hidden className="absolute inset-0 bg-neutral-950/70" />
      <div
        className={`${SECTION} relative z-10 w-full py-20 text-center md:py-28`}
      >
        <p className="type-label-md text-white/60 uppercase">
          {t(k.metabolismCtaEyebrow)}
        </p>
        <h2 className="type-display-md mx-auto mt-5 max-w-[680px] text-white">
          {t(k.metabolismCtaHeading)}
        </h2>
        <p className="type-body-lg mx-auto mt-5 max-w-[520px] text-white/70">
          {t(k.metabolismCtaBody)}
        </p>
        <div className="mt-10 flex flex-col items-center gap-6">
          <BookingLink
            href={bookingHref}
            location="metabolism-bottom"
            className={`${buttonVariants({ variant: "primary" })} w-full justify-center sm:w-auto`}
          >
            <CtaLabel
              label={t(k.metabolismCtaCta1, {
                price: bloodPriceArg(prices),
              })}
            />
          </BookingLink>
          <CtaReassurance tone="onDark" />
        </div>
      </div>
    </section>
  )
}
